import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FormLogin from "../../components/Account/formLogin";
import GoogleRolePicker from "../../components/Account/GoogleRolePicker";
import { saveRememberLogin } from "../../utils/rememberLogin";
import { markSessionActive } from "../../utils/authRoutes";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { DataContext } from "../../Provider/dataProvider";

import "../../css/account.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const getPostLoginPath = (role) => {
    const normalizedRole = String(role || '').toLowerCase();

    if (normalizedRole === 'admin') {
        return '/admin/dashboard';
    }

    if (normalizedRole === 'elderly') {
        return '/home_elderly';
    }

    return '/home_caregiver';
};

const LoginPage = () => {
    const { data, setAccessToken, clearAccessToken } = useContext(DataContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [, setFormData] = useState({ usernameOrEmail: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState('');
    const [pendingGoogle, setPendingGoogle] = useState(null);
    const [showLinkKey, setShowLinkKey] = useState(false);
    const [linkKey, setLinkKey] = useState('');
    const [pendingRole, setPendingRole] = useState(null);

    const resolvePostLoginPath = (role) => {
        const from = location.state?.from;
        if (from && typeof from === 'string' && from.startsWith('/')) {
            return from;
        }
        return getPostLoginPath(role);
    };

    useEffect(() => {
        if (!data?.id || pendingGoogle || showLinkKey) return;
        if (location.pathname === '/login' || location.pathname === '/register') return;
        navigate(resolvePostLoginPath(data.role), { replace: true });
    }, [data, navigate, location.state?.from, location.pathname, pendingGoogle, showLinkKey]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const finishLogin = (result) => {
        if (!result?.token) {
            throw new Error(result?.message || 'Đăng nhập thất bại — không nhận được phiên làm việc');
        }
        setAccessToken(result.token);
        markSessionActive();
        setPendingGoogle(null);

        const role = result.data?.role;
        const key = result.data?.linkKey;

        if (role === 'elderly' && key) {
            setLinkKey(key);
            setPendingRole(role);
            setShowLinkKey(true);
            return;
        }

        navigate(resolvePostLoginPath(role), { replace: true });
    };

    const callGoogleAuth = async (idToken, role) => {
        const response = await fetch(`${API_BASE}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken, ...(role ? { role } : {}) }),
            credentials: 'include',
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
            throw new Error(result.message || 'Đăng nhập Google thất bại');
        }
        return result;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const payload = Object.fromEntries(form.entries());
        const rememberMe = form.get('rememberMe') === '1';
        const usernameOrEmail = String(payload.usernameOrEmail || '').trim();
        const password = String(payload.password || '');
        payload.usernameOrEmail = usernameOrEmail;
        payload.rememberMe = rememberMe;

        setLoading(true);
        setError('');

        fetch(`${API_BASE}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            credentials: 'include',
        })
            .then((response) => response.json())
            .then((result) => {
                if (!result.success) {
                    throw new Error(result.message || 'Đăng nhập thất bại');
                }

                if (!result.token) {
                    throw new Error(result.message || 'Đăng nhập thất bại — không nhận được phiên làm việc');
                }
                setAccessToken(result.token);
                markSessionActive();
                saveRememberLogin(usernameOrEmail, password, rememberMe);
                navigate(resolvePostLoginPath(result.data?.role), { replace: true });
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => setLoading(false));
    };

    const handleGoogleLogin = async (idToken) => {
        setGoogleLoading(true);
        setError('');
        clearAccessToken();

        try {
            const result = await callGoogleAuth(idToken);

            if (result.needsRoleSelection) {
                setPendingGoogle({ idToken, profile: result.profile || {} });
                return;
            }

            finishLogin(result);
        } catch (err) {
            setError(err.message || 'Đăng nhập Google thất bại');
            throw err;
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleRoleConfirm = async (role) => {
        if (!pendingGoogle?.idToken) return;

        setGoogleLoading(true);
        setError('');

        try {
            const result = await callGoogleAuth(pendingGoogle.idToken, role);
            if (result.needsRoleSelection) {
                throw new Error('Không thể tạo tài khoản. Vui lòng thử đăng nhập Google lại.');
            }
            finishLogin(result);
        } catch (err) {
            setError(err.message || 'Không thể hoàn tất đăng ký Google');
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleCopyLinkKey = async () => {
        try {
            await copyToClipboard(linkKey);
        } catch {
            // ignore
        }
    };

    if (showLinkKey) {
        return (
            <div className="tamcare-auth-page">
                <div className="tamcare-auth-card tamcare-auth-card--register">
                    <div className="tamcare-auth-card__header tamcare-auth-card__header--center">
                        <h2>Đăng nhập thành công! 🎉</h2>
                        <p>Lưu mã kết nối của bạn</p>
                    </div>
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
                            Chia sẻ mã này với gia đình hoặc người chăm sóc để họ có thể theo dõi sức khỏe của bạn
                        </p>
                        <div style={{
                            background: '#f0f7ff',
                            border: '2px solid #3498db',
                            borderRadius: '8px',
                            padding: '1.5rem',
                            marginBottom: '2rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '1rem',
                        }}>
                            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3498db', fontFamily: 'monospace', letterSpacing: '2px' }}>
                                {linkKey}
                            </span>
                            <button type="button" onClick={handleCopyLinkKey} className="tamcare-linkkey-copy">
                                <i className="fa-solid fa-copy"></i> Sao chép
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(resolvePostLoginPath(pendingRole), { replace: true })}
                            className="tamcare-auth-btn"
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Vào trang chủ
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tamcare-auth-page">
            <Link to="/" className="tamcare-back-home">
                <i className="fa-solid fa-house"></i> Trang chủ
            </Link>
            <FormLogin
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                loading={loading}
                googleLoading={googleLoading}
                error={error}
                onGoogleLogin={handleGoogleLogin}
            />
            {pendingGoogle && (
                <GoogleRolePicker
                    profile={pendingGoogle.profile}
                    loading={googleLoading}
                    error={error}
                    onConfirm={handleGoogleRoleConfirm}
                    onCancel={() => {
                        setPendingGoogle(null);
                        setError('');
                    }}
                />
            )}
        </div>
    );
};

export default LoginPage;
